// Hoe zwaar mag het beeld zijn?
//
// Het dashboard hangt op meerdere schermen, en niet elk apparaat is even
// snel: op de ene Raspberry Pi loopt alles vloeiend, op de andere haakt de
// boel. Dit bestand bepaalt daarom één ding: de "trap" waarop we tekenen.
//
//   high - de dev-machine en snelle schermen: alles aan
//   mid  - een Pi die het bijhoudt: minder deeltjes, geen blur die per frame
//          opnieuw uitgerekend moet worden
//   low  - de trage Pi: alleen nog wat je vanaf de andere kant van de ruimte
//          moet kunnen zien
//
// De trap wordt op drie manieren gekozen, in deze volgorde:
//   1. handmatig: ?fx=low in de URL (blijft staan, ook na herstarten)
//   2. gemeten: loopt het beeld echt niet vloeiend, dan zakt hij zelf een
//      trapje (zie watchFrames)
//   3. geraden: aan het apparaat zelf, zodat de eerste animatie al goed gaat
//
// Wat er per trap uit gaat staat hieronder in tierSettings; de CSS leest
// dezelfde trap via het data-fx-tier attribuut op <html>.

export const tierOrder = ["high", "mid", "low"];

const settings = {
  high: {
    confettiBits: 140,
    coinCount: 28,
    gemCount: 24,
    burstSpots: 18,
    burstRings: 2,
    burstBits: 30,
    heroConfetti: 60,
    // "full" = de wijde gloed, de strakke gloed en de kop met blur
    // "light" = alleen de strakke gloed
    // "off"   = geen enkel blur-filter (de meter blijft gewoon leesbaar)
    gaugeGlow: "full",
    // De trage ademhaling van de gloed. Dit is een opacity-animatie op een
    // element met een blur-filter, en dat betekent: de blur opnieuw
    // uitrekenen, zestig keer per seconde, de hele dag door. Op een Pi is
    // dat het duurste wat er op dit scherm gebeurt.
    gaugeBreathe: true,
  },
  mid: {
    confettiBits: 70,
    coinCount: 16,
    gemCount: 14,
    burstSpots: 12,
    burstRings: 2,
    burstBits: 16,
    heroConfetti: 28,
    gaugeGlow: "light",
    gaugeBreathe: false,
  },
  low: {
    confettiBits: 32,
    coinCount: 9,
    gemCount: 8,
    burstSpots: 7,
    burstRings: 1,
    burstBits: 12,
    heroConfetti: 12,
    gaugeGlow: "off",
    gaugeBreathe: false,
  },
};

const storeKey = "tdFxTier";
const forcedKey = "tdFxTierForced";

// Zo lang wachten voor we gaan meten: de eerste seconde is het opstarten
// zelf (React, het font, de eerste ophaalronde) en die frames zijn altijd
// traag, ook op een snel apparaat.
const warmupMs = 1500;
// Zoveel frames per meting.
const sampleSize = 45;
// Boven dit gemiddelde (in ms per frame, dus ~42 beelden per seconde) vinden
// we het niet vloeiend meer.
const slowFrameMs = 24;
// En zoveel losse frames van meer dan 50ms is ook een zakker, ook als het
// gemiddelde nog meevalt: dat zijn de zichtbare haperingen.
const stutterLimit = 6;
const stutterFrameMs = 50;

let current = null;
let forced = false;
const listeners = new Set();

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Kiosk-modus zonder opslag: dan meten we elke keer opnieuw.
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Niets aan te doen, de trap geldt dan alleen deze sessie.
  }
}

function valid(tier) {
  return tierOrder.includes(tier) ? tier : null;
}

// Aan het apparaat raden. De Pi's draaien op ARM en dat is precies de groep
// die we niet op "high" willen hebben staan voor we gemeten hebben.
function guessTier() {
  const cores = window.navigator.hardwareConcurrency || 4;
  const memory = window.navigator.deviceMemory || 0;
  const agent = (window.navigator.userAgent || "").toLowerCase();
  const arm = /aarch64|armv7|armv8|arm64|raspberry|linux arm/.test(agent);

  if (cores <= 2 || (memory && memory <= 2)) return "low";
  if (arm || cores <= 4) return "mid";
  return "high";
}

// De trap in de URL wint van alles, en blijft staan: op de tv typ je niet
// makkelijk een adres, dus dat wil je één keer hoeven doen.
//   ?fx=low   vastzetten op low
//   ?fx=auto  weer laten meten
function readOverride() {
  try {
    const asked = new URLSearchParams(window.location.search).get("fx");
    if (!asked) return null;
    if (asked === "auto") {
      write(forcedKey, "");
      write(storeKey, "");
      return "auto";
    }
    const tier = valid(asked);
    if (tier) write(forcedKey, tier);
    return tier;
  } catch {
    return null;
  }
}

function resolve() {
  const override = readOverride();
  if (override && override !== "auto") {
    forced = true;
    return override;
  }

  if (override !== "auto") {
    const saved = valid(read(forcedKey));
    if (saved) {
      forced = true;
      return saved;
    }
    const measured = valid(read(storeKey));
    if (measured) return measured;
  }

  return guessTier();
}

function apply(tier) {
  current = tier;
  if (typeof document !== "undefined") {
    document.documentElement.dataset.fxTier = tier;
  }
}

// De trap ophalen. De eerste keer wordt hij bepaald en op <html> gezet.
export function renderTier() {
  if (!current) apply(resolve());
  return current;
}

// Wat er op deze trap aan staat.
export function fxSettings() {
  return settings[renderTier()];
}

export function isTierForced() {
  renderTier();
  return forced;
}

// Voor componenten die niet zelf opnieuw tekenen als de trap zakt (de meter,
// de confetti van het target). Zie useRenderTier.
export function onTierChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setRenderTier(tier, { remember = true } = {}) {
  const next = valid(tier);
  if (!next || next === renderTier()) return;

  apply(next);
  if (remember) write(storeKey, next);
  listeners.forEach((listener) => listener(next));
}

// Met de hand vastzetten: de meting laat de trap dan met rust, en hij blijft
// ook na herstarten staan. Dit is wat de knoppen in het animatielab doen en
// wat ?fx=low in de URL doet.
export function forceRenderTier(tier) {
  const next = valid(tier);
  if (!next) return;

  write(forcedKey, next);
  forced = true;
  apply(next);
  listeners.forEach((listener) => listener(next));
}

// En weer teruggeven aan de meting: alles vergeten, opnieuw raden, en de
// eerstvolgende meting bepaalt weer wat het wordt.
export function autoRenderTier() {
  write(forcedKey, "");
  write(storeKey, "");
  forced = false;

  const next = guessTier();
  apply(next);
  listeners.forEach((listener) => listener(next));
}

function stepDown() {
  const index = tierOrder.indexOf(renderTier());
  const next = tierOrder[index + 1];
  if (next) setRenderTier(next);
  return Boolean(next);
}

// Meet hoe vloeiend het beeld écht loopt en zakt een trapje als het niet
// meekomt. Eén meting per aanroep: we zakken dus stap voor stap, en pas
// nadat we het op de nieuwe trap opnieuw hebben gezien.
//
// Aanroepen op de momenten die iets zeggen: als het dashboard net staat (dan
// meten we de vaste last van de meter en de rollende lijsten) en zolang er
// een animatie loopt. Geeft een opruimfunctie terug.
export function watchFrames() {
  if (typeof window === "undefined" || isTierForced()) return () => {};
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    return () => {};
  }

  let frame = 0;
  let last = 0;
  let count = 0;
  let total = 0;
  let stutters = 0;
  // -1 en niet 0: de eerste tijdstempel die we binnenkrijgen mag zelf 0 zijn.
  let started = -1;
  let stopped = false;

  const step = (now) => {
    if (stopped) return;
    if (started < 0) started = now;

    // Het opstarten niet meerekenen.
    if (now - started < warmupMs) {
      last = now;
      frame = requestAnimationFrame(step);
      return;
    }

    if (last) {
      const delta = now - last;
      // Een sprong van meer dan een halve seconde is geen hapering maar een
      // tabblad dat weg was; die gooien we weg.
      if (delta < 500) {
        count += 1;
        total += delta;
        if (delta > stutterFrameMs) stutters += 1;
      }
    }
    last = now;

    if (count >= sampleSize) {
      const average = total / count;
      if (average > slowFrameMs || stutters >= stutterLimit) stepDown();
      return; // klaar: één meting per aanroep
    }

    frame = requestAnimationFrame(step);
  };

  frame = requestAnimationFrame(step);

  return () => {
    stopped = true;
    cancelAnimationFrame(frame);
  };
}
